import axios, { type AxiosError } from 'axios';

import { notifySessionExpired } from './sessionExpiry';
import { i18n } from '../i18n';
import { authorizationHeader } from './authToken';
import { apiBaseUrl } from '../runtimeConfig';

/*
 * REACT_APP_API_BASE_URL is the root of the server, not of one of its
 * modules: the HSAdmin Office module (`/api/hs/office`), the rbac context
 * (`/api/rbac`) and the version (`/api/version`) are siblings below
 * `/api`, and a base that already named one of them could not reach
 * the others. Only the host differs per environment (see `.env*`); the
 * shape below it is fixed and belongs here, in one place.
 */
const serverRoot = (base = apiBaseUrl()): string =>
  String(base ?? '').replace(/\/+$/, '');

/*
 * The HSAdmin HS namespace, `…/api/hs` — not one area of it. Below it sit
 * `/office/…`, `/accounts/…`, `/booking/…` and `/hosting/…`, and every
 * query in `src/api/` addresses one of those relative to this:
 *
 *   apiClient.get('/office/partners')  →  …/api/hs/office/partners
 *
 * So a whole path taken from a log or from the API definition will not
 * be found in `src/api/` — drop the `/api/hs` and search for the rest.
 * The paths stay relative because `assumedRoles.ts` matches on them to
 * decide which requests an assumed role may ride along with, and those
 * rules have no business knowing where the module is mounted.
 */
export const hsApiUrl = serverRoot() && `${serverRoot()}/api/hs`;

export const apiClient = axios.create({
  /* undefined rather than a relative `/api/hs` when nothing is
     configured: under jest the variable is unset, and a base would aim
     the queries at whatever host the test runner happens to be */
  baseURL: hsApiUrl || undefined,
});

/*
 * The URL of an endpoint that belongs to the server as a whole rather
 * than to the HSAdmin module: the version (`…/api/version`) and the
 * rbac context (`…/api/rbac/…`) are siblings of `…/api/hs`.
 *
 * The result is absolute on purpose: axios then leaves the base URL
 * out of it, and the assumed-roles interceptor, whose rules all match
 * paths below the base, cannot attach a role to such a request.
 */
export const apiSiblingUrl = (path: string, base?: string): string =>
  `${serverRoot(base)}/api${path}`;

/*
 * An env file written before this variable meant the server root names
 * a module (`…/api/hs`). Left alone it would build
 * `…/api/hs/api/hs/office/…`, and every request would miss a path that
 * looks almost right — the kind of misconfiguration that otherwise only
 * shows up as data that never arrives.
 */
if (/\/api(\/|$)/.test(serverRoot())) {
  console.error(
    `REACT_APP_API_BASE_URL must be the root of the server, not a module below it: got "${serverRoot()}", expected something like "http://127.0.0.1:8080/" — refer to .env.example`
  );
}

apiClient.interceptors.request.use((config) => {
  const authorization = authorizationHeader();
  if (authorization) {
    config.headers.Authorization = authorization;
  }

  /* tell the backend which language to localize its responses in (e.g.
     error messages): the language chosen in the app, not just whatever
     Accept-Language the browser would send on its own */
  const language = i18n.resolvedLanguage || i18n.language;
  if (language) {
    config.headers['Accept-Language'] = language;
  }

  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    /* an expired/invalid JWT — let the auth provider renew or re-login */
    if (error.response?.status === 401) {
      notifySessionExpired();
    }
    return Promise.reject(error);
  }
);
